import React, { Component } from 'react';

import cloneDeep from 'lodash/cloneDeep';
import xss from 'xss';

import {
  TestExecutionCycleNavigation,
  TestExecutionCycleNavigationBanner,
  TestExecutionDetails,
  TestExecutionHeader,
  UserPicker,
} from '@qality/common-components';
import { PremiumFeatureGuard } from '@qality/common-components/src/atoms/PremiumFeatureGuard';
import { QAlityFreeLabel } from '@qality/common-components/src/atoms/QAlityFreeLabel';
import { Text } from '@qality/common-components/src/atoms/Text';
import { ExecutionHistoryProvider } from '@qality/common-components/src/lib/ExecutionHistoryProvider';
import InlineEdit from '@qality/common-components/src/molecules/inline-edit';
import { PreviousTestExecutions } from '@qality/common-components/src/organisms/PreviousTestExecutions';
import { QAlityFreePreviousTestExecutions } from '@qality/common-components/src/organisms/QAlityFreePreviousTestExecutions';
import { TestExecutionListBanner } from '@qality/common-components/src/organisms/TestExecutionListBanner';
import { ExecutionStatusSelect } from '@qality/common-components/src/select/execution-status-select/ExecutionStatusSelect';
import CustomStatusesInfo from '@qality/common-components/src/test-execution/CustomStatusesInfo';
import {
  FEATURE_FLAG,
  PREMIUM_FEATURE_TOOLTIP_MESSAGE,
  UPGRADE_TO_QALITY_PLUS_TOOLTIP_MESSAGE,
} from '@qality/common-config';
import { messages } from '@qality/common-messages';
import {
  TestExecution,
  TestExecutionStep,
  TestStep,
} from '@qality/common-model';
import {
  FlagService,
  StorageOfUniqueModels,
  StorageOfUniqueServices,
  TestExecutionService,
} from '@qality/common-service';
import { TestExecutionPageContext } from '@qality/common-state';
import { InputUtils, StringUtils } from '@qality/common-utils';

import './_test-execution.scss';

export default class TestCaseExecutionPage extends Component<
  Record<string, never>
> {
  static contextType = TestExecutionPageContext;
  constructor() {
    super();
    this.state = {
      inlineEditKey: 0,
    };
  }

  updateTestExecutionComment = async (value: string) => {
    const valueWithEmptyLines = InputUtils.keepEmptyLines(value);
    if (this.context.testExecution.comment != valueWithEmptyLines) {
      try {
        let updatedTestExecution = cloneDeep(this.context.testExecution);
        updatedTestExecution.comment = valueWithEmptyLines;
        updatedTestExecution =
          this.autoUpdateExecutionAssignee(updatedTestExecution);
        this.context.handleUpdateTestExecution(updatedTestExecution);
        this.setState({
          inlineEditKey: this.state.inlineEditKey + 1,
        });
        await TestExecutionService.updateTestExecution(updatedTestExecution);
      } catch (e) {
        StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
          e,
          messages[
            'error.testCaseExecutionPage.updateTestExecutionComment.message'
          ]
        );
      }
    } else {
      this.setState({
        inlineEditKey: this.state.inlineEditKey + 1,
      });
    }
  };

  updateTestExecutionStatus = async (status: any) => {
    try {
      let updatedTestExecution = cloneDeep(this.context.testExecution);
      updatedTestExecution.status = status;
      updatedTestExecution =
        this.autoUpdateExecutionAssignee(updatedTestExecution);
      this.context.handleUpdateTestExecution(updatedTestExecution);
      await TestExecutionService.updateTestExecution(updatedTestExecution);
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages[
          'error.testCaseExecutionPage.updateTestExecutionStatus.message'
        ]
      );
    }
  };

  updateTestSteps = async (
    testSteps: Array<{ index: number; testStep: TestExecutionStep }>
  ) => {
    let updatedTestExecution = cloneDeep(this.context.testExecution);

    testSteps.forEach((testStep) => {
      updatedTestExecution.testSteps[testStep.index] = testStep.testStep;
    });

    TestExecutionService.updateTestExecutionSteps(
      testSteps.map((ts) => ts.testStep)
    );

    updatedTestExecution = this.autoUpdateStatus(updatedTestExecution);

    updatedTestExecution =
      this.autoUpdateExecutionAssignee(updatedTestExecution);

    this.context.handleUpdateTestExecution(updatedTestExecution);

    TestExecutionService.updateTestExecution(updatedTestExecution, true);
  };

  updateTestStep = async (
    testStepIndex: number,
    testStep: TestExecutionStep
  ) => {
    let updatedTestExecution = cloneDeep(this.context.testExecution);
    updatedTestExecution.testSteps[testStepIndex] = testStep;
    if (testStep.isStatusUpdate) {
      updatedTestExecution.testSteps[testStepIndex].isStatusUpdate = false;
      updatedTestExecution = this.autoUpdateStatus(updatedTestExecution);
    }

    TestExecutionService.updateTestExecutionSteps([testStep]);

    updatedTestExecution =
      this.autoUpdateExecutionAssignee(updatedTestExecution);

    this.context.handleUpdateTestExecution(updatedTestExecution);

    TestExecutionService.updateTestExecution(updatedTestExecution, true);
  };

  autoUpdateStatus = (testExecution: TestExecution) => {
    if (testExecution.testSteps.length === 0) {
      return testExecution;
    }

    const isEveryTestStepPassed = testExecution.testSteps.every(
      (testStep) => testStep.status.category === 'PASSED'
    );

    const firstTestStep = testExecution.testSteps[0];

    const isEveryTestStepStatusSame = testExecution.testSteps.every(
      (testStep) => testStep.status.id === firstTestStep.status.id
    );

    const areExecutionAndTestStepsStatusesDifferent =
      testExecution.status.id !== firstTestStep.status.id;

    if (
      isEveryTestStepPassed &&
      isEveryTestStepStatusSame &&
      areExecutionAndTestStepsStatusesDifferent
    ) {
      testExecution.status = structuredClone(firstTestStep.status);

      FlagService.createSuccessFlag(
        'Success',
        `The status of your test case execution was changed to ${testExecution.status.name}`
      );
    }

    return testExecution;
  };

  autoUpdateExecutionAssignee = (testExecution: TestExecution) => {
    if (!testExecution.executionAssignee?.getIdentifier()) {
      testExecution.executionAssignee = this.context.currentUser;
    }
    return testExecution;
  };

  updateTestStepInState = (testStepIndex: number, testStep: TestStep) => {
    const updatedTestExecution = cloneDeep(this.context.testExecution);
    updatedTestExecution.testSteps[testStepIndex] = testStep;
    this.context.handleUpdateTestExecution(updatedTestExecution);
  };

  getTestExecutionDescription() {
    let description =
      this.context.testExecution.testCaseIssue.renderedFields.description;

    if (description) {
      // since we are in iframe and want a new tab
      description = description.replace(
        /<a /g,
        '<a target="_blank" referrerPolicy="no-referrer" '
      );

      // the link starts from /secure/attachment/{attackement id}/{attackement id}_{file name}.png
      // so we have to add hostname
      description = description.replace(
        /<img src="/g,
        `<img src="${StorageOfUniqueServices.get(
          'HostNameService'
        ).getIssueDescriptionHost()}`
      );
    }

    return xss(description);
  }

  handleExecutionAssigneeChange = async (newExecutionAssignee) => {
    try {
      const updatedTestExecution = cloneDeep(this.context.testExecution);
      updatedTestExecution.executionAssignee = newExecutionAssignee.user;
      this.context.handleUpdateTestExecution(updatedTestExecution);
      await TestExecutionService.updateExecutionAssignee(
        updatedTestExecution.id,
        updatedTestExecution.executionAssignee?.[
          StorageOfUniqueModels.get('IDENTIFIER')
        ]
      );
    } catch (e) {
      const message =
        e.response?.data?.message ||
        messages[
          'error.testCaseExecutionPage.updateTestExecutionStatus.message'
        ];
      FlagService.createErrorFlag('Error', message);
    }
  };

  handleAddBug = async () => {
    let updatedTestExecution = cloneDeep(this.context.testExecution);
    updatedTestExecution =
      this.autoUpdateExecutionAssignee(updatedTestExecution);
    this.context.handleUpdateTestExecution(updatedTestExecution);
    await TestExecutionService.updateTestExecution(updatedTestExecution, true);
  };

  // eslint-disable-next-line complexity
  render() {
    StorageOfUniqueServices.get('AP').sizeToParent(true);
    const { key: issueKey } = this.context.testExecution.testCaseIssue;
    const description = this.getTestExecutionDescription();

    return (
      <div>
        <PremiumFeatureGuard
          premium={
            <TestExecutionCycleNavigation
              testCycle={this.context.testExecution.testCycle}
              adjoiningTestCasesInCycle={
                this.context.testExecution.adjoiningTestCasesInCycle
              }
            />
          }
          free={
            <TestExecutionCycleNavigationBanner
              isInTestCycle={!!this.context.testExecution.testCycle}
            />
          }
        />
        <div className='test-case-execution-page'>
          <TestExecutionHeader
            testExecutionName={this.context.testExecution.name}
            testCaseName={
              this.context.testExecution.testCaseIssue.fields.summary
            }
            testCaseIssueKey={this.context.testExecution.testCaseIssue.key}
            testCaseIssueTypeIcon={
              this.context.testExecution.testCaseIssue.fields.issuetype.iconUrl
            }
            testCycle={this.context.testExecution.testCycle}
            testExecutionId={this.context.testExecution.id}
            testType={this.context.testExecution.testType}
          />
          <div className='test-case-execution-page-execution-assignee'>
            <div className='qality-label'>Execution Assignee</div>
            <UserPicker
              key={this.context.testExecution.executionAssignee?.getIdentifier()}
              userId={this.context.testExecution.executionAssignee?.getIdentifier()}
              onChange={this.handleExecutionAssigneeChange}
              isDisabled={!FEATURE_FLAG.isPremium || !this.context.canEdit}
              projectKey={
                this.context.testExecution.testCaseIssue.fields.project.key
              }
              tooltip={
                FEATURE_FLAG.isPremium
                  ? undefined
                  : UPGRADE_TO_QALITY_PLUS_TOOLTIP_MESSAGE
              }
            />
            {!FEATURE_FLAG.isPremium && (
              <QAlityFreeLabel
                text={PREMIUM_FEATURE_TOOLTIP_MESSAGE}
                linkName='Learn more'
                linkUrl=' https://soldevelo.atlassian.net/wiki/spaces/QS/pages/2444165210/QAlity+vs+QAlity+Plus'
              />
            )}
          </div>
          <div className='test-case-execution-page-wrapper hide-focus-wrapper'>
            <div className='test-case-execution-page-description'>
              <div className='qality-label'>{issueKey} Description</div>
              {description ? (
                <div
                  className='text-wrapper'
                  dangerouslySetInnerHTML={{
                    __html: description,
                  }}
                />
              ) : (
                <Text inactive>None</Text>
              )}
            </div>
            <div className='test-case-execution-page-comment'>
              <div className='qality-label'>Execution Comment</div>
              {this.context.canEdit ? (
                <InlineEdit
                  key={this.state.inlineEditKey}
                  placeholder='Add comment'
                  text={this.context.testExecution.comment}
                  onConfirm={this.updateTestExecutionComment}
                  length={10000}
                />
              ) : (
                <div
                  className='text-wrapper'
                  dangerouslySetInnerHTML={{
                    __html: StringUtils.toHTML(
                      this.context.testExecution.comment
                    ),
                  }}
                />
              )}
            </div>
          </div>
          <div className='test-case-execution-table-wrapper'>
            <div className='test-case-execution-page-status-container'>
              <ExecutionStatusSelect
                value={this.context.testExecution.status}
                statuses={this.context.statuses.allStatuses}
                onChange={this.updateTestExecutionStatus}
                isDisabled={!this.context.canEdit}
                isLoading={false}
              />
              <CustomStatusesInfo />
            </div>
            <TestExecutionDetails
              testExecutionId={this.context.testExecution.id}
              testSteps={this.context.testExecution.testSteps}
              testCaseIssueKey={this.context.testExecution.testCaseIssue.key}
              onBulkUpdateTestStep={this.updateTestSteps}
              onUpdateTestStep={this.updateTestStep}
              onUpdateTestStepSelection={this.updateTestStepInState}
              onAddBug={this.handleAddBug}
            />
          </div>
          {FEATURE_FLAG.isPremium ? (
            <ExecutionHistoryProvider issueKey={issueKey}>
              <PreviousTestExecutions />
            </ExecutionHistoryProvider>
          ) : (
            <>
              <div className='test-case-execution-page-wrapper-past-executions'>
                <div className='qality-test-executions-container'>
                  <QAlityFreePreviousTestExecutions issueKey={issueKey} />
                </div>
                <TestExecutionListBanner showBanner={!FEATURE_FLAG.isPremium} />
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}
